import { useRef } from 'react';
import { motion, useInView } from 'motion/react';
import { Link } from 'react-router-dom';
import { FloatingNav, OverlaySideNav, HeroSection as LayoutHeroSection, NavSection } from '@/components/layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { cn } from '@/lib/utils';
import type { EmploymentStatus } from '@/data/types';
import {
  Target,
  Users,
  ChevronRight,
  Calculator,
  Calendar,
  Shield,
  AlertTriangle,
  Percent,
  ClipboardList,
  CheckCircle,
  XCircle,
  Eye,
  BarChart3,
  Briefcase,
  ArrowRight,
  HelpCircle,
  Building2,
  Key,
  MessageSquare,
  Unlock,
} from 'lucide-react';

// ============================================================================
// Navigation Sections
// ============================================================================

const navSections: NavSection[] = [
  { id: 'your-role', label: 'Your Role', icon: <Users className="w-4 h-4" /> },
  { id: 'simple-framework', label: 'The Framework', icon: <Key className="w-4 h-4" /> },
  { id: 'detailed-context', label: 'Detailed Context', icon: <ClipboardList className="w-4 h-4" /> },
  { id: 'next-steps', label: 'Next Steps', icon: <ChevronRight className="w-4 h-4" /> },
];

// ============================================================================
// Animation Variants
// ============================================================================

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

// ============================================================================
// Section Wrapper Component
// ============================================================================

function Section({
  id,
  children,
  className,
  background = 'white',
}: {
  id: string;
  children: React.ReactNode;
  className?: string;
  background?: 'white' | 'gray';
}): React.ReactElement {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <section
      id={id}
      ref={ref}
      className={cn(
        'py-12 md:py-16 scroll-mt-20',
        background === 'gray' ? 'bg-gray-50' : 'bg-white',
        className
      )}
    >
      <motion.div
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
        variants={fadeInUp}
        className="max-w-6xl mx-auto px-6"
      >
        {children}
      </motion.div>
    </section>
  );
}

// ============================================================================
// Hero Section
// ============================================================================

function HeroSection(): React.ReactElement {
  return (
    <LayoutHeroSection
      role="physician"
      badgeText="Physician Leadership"
      title="Leader Support Guide"
      description="Supporting Primary Care Physician Success in EQuIP 2026"
      subtitle="Primary Care CMO and State CMOs—those responsible for physician performance and consistent messaging across regions."
    />
  );
}

// ============================================================================
// TIER 1: Your Role in 60 Seconds
// ============================================================================

function YourRoleSection(): React.ReactElement {
  return (
    <Section id="your-role">
      <div className="text-center mb-8">
        <Badge className="bg-eventus-purple/10 text-eventus-purple mb-4">Leader Essentials</Badge>
        <h2 className="text-3xl md:text-4xl font-bold text-eventus-navy mb-4">
          Your Role in 60 Seconds
        </h2>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <Card className="border-2 border-green-500/30 bg-green-50">
          <CardContent className="p-6">
            <CheckCircle className="w-8 h-8 text-green-600 mb-3" />
            <h3 className="font-bold text-eventus-navy mb-2">Your Job</h3>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>Explain the program clearly</li>
              <li>Support understanding</li>
              <li>Use data proactively</li>
              <li>Answer questions with talking scripts</li>
            </ul>
          </CardContent>
        </Card>

        <Card className="border-2 border-red-500/30 bg-red-50">
          <CardContent className="p-6">
            <XCircle className="w-8 h-8 text-red-600 mb-3" />
            <h3 className="font-bold text-eventus-navy mb-2">Not Your Job</h3>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>Grant exceptions</li>
              <li>Negotiate targets</li>
              <li>Make eligibility decisions</li>
            </ul>
          </CardContent>
        </Card>

        <Card className="border-2 border-blue-500/30 bg-blue-50">
          <CardContent className="p-6">
            <ArrowRight className="w-8 h-8 text-blue-600 mb-3" />
            <h3 className="font-bold text-eventus-navy mb-2">Redirect To</h3>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>HR for program design questions</li>
              <li>Dashboard for real-time data</li>
              <li>This guide for talking scripts</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </Section>
  );
}

// ============================================================================
// TIER 2: The Simple Framework
// ============================================================================

function SimpleFrameworkSection(): React.ReactElement {
  return (
    <Section id="simple-framework" background="gray">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        The Simple Framework
      </h2>
      <p className="text-gray-600 mb-6 text-lg">
        Explain the program in three categories. This is the foundation—everything else builds on this.
      </p>

      {/* Visual Flow */}
      <Card className="mb-8 border-2 border-eventus-purple/20">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4">
            <div className="bg-eventus-purple/10 rounded-lg p-4 text-center flex-1">
              <Key className="w-6 h-6 text-eventus-purple mx-auto mb-2" />
              <p className="font-bold text-eventus-navy">1. Eligibility Measure</p>
              <p className="text-sm text-gray-600">ACO Visits</p>
              <p className="text-eventus-purple font-medium">Quarterly target</p>
            </div>
            <ArrowRight className="w-6 h-6 text-gray-400 hidden md:block" />
            <div className="bg-green-50 rounded-lg p-4 text-center flex-1">
              <span className="text-green-600 font-bold">2. Consistency</span>
              <p className="text-sm text-gray-600">3 measures</p>
              <p className="text-green-700 font-medium">40% total</p>
            </div>
            <span className="text-gray-400 hidden md:block">+</span>
            <div className="bg-blue-50 rounded-lg p-4 text-center flex-1">
              <span className="text-blue-600 font-bold">3. Outcomes</span>
              <p className="text-sm text-gray-600">Q2+ only</p>
              <p className="text-blue-700 font-medium">60% total</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key Message */}
      <Card className="mt-6 bg-eventus-navy text-white">
        <CardContent className="p-4">
          <p className="font-medium">
            <strong>The key message:</strong> "Meet the eligibility measure, then each earning measure you meet adds to your award independently."
          </p>
        </CardContent>
      </Card>
    </Section>
  );
}

// ============================================================================
// TIER 3: Detailed Context (Collapsed)
// ============================================================================

function DetailedContextSection({ employmentStatus }: { employmentStatus?: EmploymentStatus }): React.ReactElement {
  return (
    <Section id="detailed-context">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        Detailed Context
      </h2>
      <p className="text-gray-600 mb-6">
        Deeper reference material for complex questions. Expand as needed.
      </p>

      <Accordion type="single" collapsible className="space-y-3">
        {/* Six Principles */}
        <AccordionItem value="six-principles" className="border rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-eventus-purple/10 flex items-center justify-center flex-shrink-0">
                <ClipboardList className="w-4 h-4 text-eventus-purple" />
              </div>
              <span className="font-semibold text-eventus-navy">The Six Principles</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <SixPrinciplesContent />
          </AccordionContent>
        </AccordionItem>

        {/* 60/40 Structure */}
        <AccordionItem value="sixty-forty" className="border rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-eventus-purple/10 flex items-center justify-center flex-shrink-0">
                <Percent className="w-4 h-4 text-eventus-purple" />
              </div>
              <span className="font-semibold text-eventus-navy">60/40 Structure Details</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <SixtyFortyContent />
          </AccordionContent>
        </AccordionItem>

        {/* ACO Visits Deep Dive */}
        <AccordionItem value="aco-visits" className="border rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-eventus-purple/10 flex items-center justify-center flex-shrink-0">
                <Key className="w-4 h-4 text-eventus-purple" />
              </div>
              <span className="font-semibold text-eventus-navy">ACO Visits Deep Dive</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ACOVisitsContent />
          </AccordionContent>
        </AccordionItem>

        {/* Consistency Floor */}
        <AccordionItem value="consistency-floor" className="border rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-eventus-purple/10 flex items-center justify-center flex-shrink-0">
                <Shield className="w-4 h-4 text-eventus-purple" />
              </div>
              <span className="font-semibold text-eventus-navy">Consistency Floor</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ConsistencyFloorContent employmentStatus={employmentStatus} />
          </AccordionContent>
        </AccordionItem>

        {/* Attribution & Data */}
        <AccordionItem value="attribution" className="border rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-eventus-purple/10 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-4 h-4 text-eventus-purple" />
              </div>
              <span className="font-semibold text-eventus-navy">Attribution & Data Accuracy</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <AttributionContent />
          </AccordionContent>
        </AccordionItem>

        {/* Communication Cadence */}
        <AccordionItem value="communication" className="border rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-eventus-purple/10 flex items-center justify-center flex-shrink-0">
                <Calendar className="w-4 h-4 text-eventus-purple" />
              </div>
              <span className="font-semibold text-eventus-navy">Communication Cadence</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <CommunicationContent />
          </AccordionContent>
        </AccordionItem>

        {/* Why This Program Exists */}
        <AccordionItem value="why-exists" className="border rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-eventus-purple/10 flex items-center justify-center flex-shrink-0">
                <Target className="w-4 h-4 text-eventus-purple" />
              </div>
              <span className="font-semibold text-eventus-navy">Why This Program Exists</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <WhyExistsContent />
          </AccordionContent>
        </AccordionItem>

        {/* Leader Roles */}
        <AccordionItem value="leader-roles" className="border rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-eventus-purple/10 flex items-center justify-center flex-shrink-0">
                <Briefcase className="w-4 h-4 text-eventus-purple" />
              </div>
              <span className="font-semibold text-eventus-navy">Leader Roles & Responsibilities</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <LeaderRolesContent />
          </AccordionContent>
        </AccordionItem>

        {/* Dashboards */}
        <AccordionItem value="dashboards" className="border rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-eventus-purple/10 flex items-center justify-center flex-shrink-0">
                <BarChart3 className="w-4 h-4 text-eventus-purple" />
              </div>
              <span className="font-semibold text-eventus-navy">Using Dashboards</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <DashboardsContent />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Section>
  );
}

// ============================================================================
// Detailed Context Content Components
// ============================================================================

function SixPrinciplesContent(): React.ReactElement {
  const principles = [
    {
      id: 1,
      title: 'Foundation First',
      description: 'ACO Visits is your eligibility foundation. Meet the ACO Visits measure to unlock your incentive opportunity. Once eligible, each remaining measure stands on its own.',
      script: 'Your ACO Visits rate is the starting point. Meet that, and you unlock the full incentive opportunity. From there, each measure you meet adds to your total.'
    },
    {
      id: 2,
      title: 'Universal Structure',
      description: 'The 60/40 model applies to everyone—physicians and APCs, full-time and part-time. Outcome-measure targets graduate across the year; consistency measures hold at a single target.',
      script: 'Same rules for everyone. Consistency not just in measures but also in design.'
    },
    {
      id: 3,
      title: 'Equal Division',
      description: 'The 60% outcome portion is always divided equally among active measures.',
      script: 'Each measure carries the same weight. Equal division means consistent structure.'
    },
    {
      id: 4,
      title: 'Fair Scaling',
      description: 'Part-time physicians have a lower maximum award but are held to the same quality standards.',
      script: 'Lower cap, same standards. It\'s proportional.'
    },
    {
      id: 5,
      title: 'Consistency Is Your Foundation',
      description: 'Once you meet the ACO Visits measure, strong consistency performance is always available to you. In Q1, consistency measures represent your full opportunity.',
      script: 'After meeting your ACO Visits target, consistency is guaranteed opportunity. In Q1, that\'s your entire program.'
    },
    {
      id: 6,
      title: 'Patient-Centered',
      description: 'Physicians are measured on outcomes at their assigned facilities.',
      script: 'Your facilities, your patients, your numbers.'
    }
  ];

  return (
    <div className="space-y-4">
      <p className="text-gray-600">
        These six principles answer 80% of the questions your team will ask.
      </p>
      {principles.map((principle) => (
        <div key={principle.id} className="p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-full bg-eventus-purple/10 flex items-center justify-center">
              <span className="text-xs font-bold text-eventus-purple">{principle.id}</span>
            </div>
            <h4 className="font-semibold text-eventus-navy">{principle.title}</h4>
          </div>
          <p className="text-sm text-gray-600 mb-2">{principle.description}</p>
          <div className="p-2 bg-eventus-purple/5 rounded border-l-2 border-eventus-purple">
            <p className="text-xs text-gray-500 mb-1">What to say:</p>
            <p className="text-sm text-gray-700 italic">"{principle.script}"</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function SixtyFortyContent(): React.ReactElement {
  return (
    <div className="space-y-5">
      {/* THE HOOK: Lead with the positive outcome */}
      <div className="p-4 bg-eventus-purple/5 border-l-4 border-eventus-purple rounded-r-lg">
        <p className="text-eventus-navy font-semibold">
          Meet your ACO Visits target, and you unlock the full 60/40 incentive structure.
        </p>
        <p className="text-gray-600 text-sm mt-1">
          ACO Visits is your foundation. Once met, every measure you achieve adds to your award.
        </p>
      </div>

      {/* VISUAL FLOW: Two-step process */}
      <div className="space-y-3">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">How It Works</p>

        {/* Step 1: The Foundation */}
        <div className="flex items-stretch gap-3">
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-eventus-purple flex items-center justify-center text-white font-bold text-sm">
              1
            </div>
            <div className="flex-1 w-0.5 bg-eventus-purple/30 my-1" />
          </div>
          <div className="flex-1 pb-4">
            <div className="flex items-center gap-2 mb-2">
              <Key className="w-4 h-4 text-eventus-purple" />
              <span className="font-semibold text-eventus-navy">Establish Your Foundation</span>
              <Badge className="bg-eventus-purple/10 text-eventus-purple text-xs">ACO Visits</Badge>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-4 text-sm">
                <div className="text-center">
                  <span className="text-warning font-bold">Ramp-up</span>
                  <p className="text-xs text-gray-500">Q1</p>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-300" />
                <div className="text-center">
                  <span className="text-eventus-purple font-bold">Standard</span>
                  <p className="text-xs text-gray-500">Q2-Q4</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                The Q1 ramp-up gives you time to build momentum with your ACO panel.
              </p>
            </div>
          </div>
        </div>

        {/* Step 2: The Split */}
        <div className="flex items-stretch gap-3">
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-success flex items-center justify-center text-white font-bold text-sm">
              2
            </div>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Unlock className="w-4 h-4 text-success" />
              <span className="font-semibold text-eventus-navy">Access the 60/40 Structure</span>
              <Badge className="bg-success/10 text-success text-xs">Q2-Q4</Badge>
            </div>

            {/* 60/40 Visual Bar */}
            <div className="flex rounded-lg overflow-hidden">
              <div className="w-[60%] bg-gray-500 text-white p-3">
                <div className="text-xl font-bold">60%</div>
                <div className="text-sm font-semibold">Outcomes</div>
              </div>
              <div className="w-[40%] bg-eventus-purple text-white p-3">
                <div className="text-xl font-bold">40%</div>
                <div className="text-sm font-semibold">Consistency</div>
              </div>
            </div>

            {/* Measure Details */}
            <div className="grid md:grid-cols-2 gap-3 mt-3">
              <div className="p-3 bg-gray-100 rounded-lg">
                <h4 className="font-semibold text-gray-700 text-sm mb-1">Outcome Measures</h4>
                <p className="text-xs text-gray-600">A1C, BCS, Colorectal, PHQ-9, HTN</p>
                <p className="text-xs text-gray-500 mt-1">Divided equally among active measures</p>
              </div>
              <div className="p-3 bg-eventus-purple/10 rounded-lg">
                <h4 className="font-semibold text-eventus-purple text-sm mb-1">Consistency Measures</h4>
                <p className="text-xs text-gray-600">PIMs, Coding Accuracy, Documentation</p>
                <p className="text-xs text-gray-500 mt-1">Available every quarter once eligible</p>
              </div>
            </div>
          </div>
        </div>

        {/* Q1 Callout */}
        <div className="p-3 bg-warning/10 rounded-lg border border-warning/20 mt-2">
          <p className="text-sm text-gray-700">
            <span className="font-semibold text-warning">Q1 is your foundation quarter.</span>{' '}
            After meeting the Q1 ACO Visits target, your Q1 award comes from the three Consistency Measures.
            Outcome measures activate in Q2, when the full 60/40 structure applies.
          </p>
        </div>
      </div>

      {/* THE BOTTOM LINE: Key takeaway */}
      <Card className="bg-gray-50 border-2 border-gray-200">
        <CardContent className="p-4">
          <h4 className="font-semibold text-eventus-navy mb-3 flex items-center gap-2">
            <Target className="w-4 h-4 text-eventus-purple" />
            The Bottom Line
          </h4>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
              <span><strong>Foundation first, then earning.</strong> ACO Visits establishes eligibility; it's not part of the 60/40 calculation.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
              <span><strong>Each measure you meet adds to your award.</strong> Strong performance in one area supports your total.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
              <span><strong>Outcome measures graduate across the year; consistency measures hold at a single target.</strong> Predictable ramp, same standard by year-end.</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Talking Script */}
      <div className="p-3 bg-eventus-purple/5 rounded border-l-2 border-eventus-purple">
        <p className="text-xs text-gray-500 mb-1">What to say:</p>
        <p className="text-sm text-gray-700 italic">
          "Meet your ACO Visits target, and you unlock the full 60/40 structure. From there, each measure you meet adds to your quarterly award. The more you achieve, the more you earn."
        </p>
      </div>
    </div>
  );
}

function ACOVisitsContent(): React.ReactElement {
  return (
    <div className="space-y-4">
      <div className="flex gap-2 mb-2">
        <Badge className="bg-eventus-purple/10 text-eventus-purple">Physician-Specific</Badge>
        <Badge className="bg-eventus-navy/10 text-eventus-navy">Eligibility Measure</Badge>
      </div>

      <p className="text-gray-600">
        ACO Visits determines your eligibility for any incentive each quarter. Meet the measure, and your full incentive opportunity unlocks.
      </p>

      <div className="grid grid-cols-4 gap-2">
        {[
          { quarter: 'Q1', label: 'Ramp-up' },
          { quarter: 'Q2', label: 'Standard' },
          { quarter: 'Q3', label: 'Standard' },
          { quarter: 'Q4', label: 'Standard' }
        ].map((q) => (
          <div
            key={q.quarter}
            className={cn(
              'p-3 rounded-lg text-center',
              q.quarter === 'Q1'
                ? 'bg-warning/10 border-2 border-warning/30'
                : 'bg-eventus-purple/10 border-2 border-eventus-purple/30'
            )}
          >
            <p className="text-xs font-medium text-gray-500">{q.quarter}</p>
            <p className={cn(
              'text-sm font-bold mt-1',
              q.quarter === 'Q1' ? 'text-warning' : 'text-eventus-purple'
            )}>{q.label}</p>
          </div>
        ))}
      </div>

      <Card className="bg-eventus-purple/5 border-eventus-purple/20">
        <CardContent className="p-4">
          <h4 className="font-semibold text-eventus-navy mb-2">Key Points</h4>
          <ul className="space-y-2 text-gray-700 text-sm">
            <li className="flex items-start gap-2">
              <Key className="w-4 h-4 text-eventus-purple mt-0.5 flex-shrink-0" />
              <span>ACO Visits is your eligibility foundation—meet it to unlock your incentive opportunity</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
              <span>ACO Visits is unique to physicians—APCs do not have this measure</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
              <span>Q1 uses a ramp-up target to allow workflow adjustment</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

function ConsistencyFloorContent({ employmentStatus }: { employmentStatus?: EmploymentStatus }): React.ReactElement {
  return (
    <div className="space-y-4">
      <p className="text-gray-600">
        After meeting your ACO Visits eligibility measure, consistency measures provide a reliable foundation. In Q1, consistency represents your entire opportunity.
      </p>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 text-gray-500">Employment</th>
              <th className="text-center py-2 text-gray-500">Maximum Award</th>
              <th className="text-center py-2 text-gray-500">Consistency Floor (40%)</th>
            </tr>
          </thead>
          <tbody>
            {employmentStatus !== 'part-time' && (
              <tr className="border-b">
                <td className="py-2 font-medium">Full-Time (5 days)</td>
                <td className="text-center py-2">$4,000</td>
                <td className="text-center py-2 font-bold text-eventus-purple">$1,600</td>
              </tr>
            )}
            {employmentStatus !== 'full-time' && (
              <>
                <tr className="border-b">
                  <td className="py-2 font-medium">Part-Time (4 days)</td>
                  <td className="text-center py-2">$3,200</td>
                  <td className="text-center py-2 font-bold text-eventus-purple">$1,280</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 font-medium">Part-Time (3 days)</td>
                  <td className="text-center py-2">$2,400</td>
                  <td className="text-center py-2 font-bold text-eventus-purple">$960</td>
                </tr>
                <tr>
                  <td className="py-2 font-medium">Part-Time (2 days)</td>
                  <td className="text-center py-2">$1,600</td>
                  <td className="text-center py-2 font-bold text-eventus-purple">$640</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      <Card className="bg-eventus-purple/5 border-eventus-purple/20">
        <CardContent className="p-4 flex items-start gap-3">
          <Shield className="w-5 h-5 text-eventus-purple flex-shrink-0 mt-0.5" />
          <p className="text-gray-700 text-sm">
            <strong>Key point:</strong> Consistency is always worth pursuing, regardless of outcome performance. It represents meaningful earning potential when you maintain strong PIMs, coding, and documentation.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function AttributionContent(): React.ReactElement {
  return (
    <div className="space-y-4">
      <p className="text-gray-600">
        Physicians are measured on outcomes at their assigned facilities—not a team average.
      </p>

      <div className="flex flex-wrap items-center justify-center gap-2 p-4 bg-gray-50 rounded-lg">
        {[
          { label: 'PHYSICIAN', sublabel: '(GLR)' },
          { label: 'FACILITIES', sublabel: '(GLR)' },
          { label: 'PATIENTS', sublabel: '(At Facilities)' }
        ].map((step, index) => (
          <div key={step.label} className="flex items-center gap-2">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-eventus-purple/10 flex items-center justify-center mx-auto">
                {index === 0 && <Users className="w-5 h-5 text-eventus-purple" />}
                {index === 1 && <Building2 className="w-5 h-5 text-eventus-purple" />}
                {index === 2 && <Users className="w-5 h-5 text-eventus-purple" />}
              </div>
              <p className="mt-1 text-xs font-medium text-gray-700">{step.label}</p>
              <p className="text-xs text-gray-500">{step.sublabel}</p>
            </div>
            {index < 2 && <ArrowRight className="w-4 h-4 text-gray-400" />}
          </div>
        ))}
      </div>

      <Card className="border-2 border-warning/30">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-warning" />
            <h4 className="font-semibold text-eventus-navy">Data Accuracy Is a Shared Responsibility</h4>
          </div>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="font-medium text-gray-700 mb-1">STATE CMOs:</p>
              <ul className="text-gray-600 space-y-1">
                <li>Verify GLR assignments</li>
                <li>Flag coverage situations</li>
                <li>Escalate issues early</li>
              </ul>
            </div>
            <div>
              <p className="font-medium text-gray-700 mb-1">PHYSICIANS:</p>
              <ul className="text-gray-600 space-y-1">
                <li>Review facility assignments</li>
                <li>Report incorrect facilities</li>
              </ul>
            </div>
            <div>
              <p className="font-medium text-gray-700 mb-1">SUPPORT TEAMS:</p>
              <ul className="text-gray-600 space-y-1">
                <li>Keep GLR accurate</li>
                <li>Process changes promptly</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function CommunicationContent(): React.ReactElement {
  return (
    <div className="space-y-4">
      <p className="text-gray-600">
        Consistent communication is essential. There's a rhythm to the quarter: expectations at the start, check-ins during, and results at the end.
      </p>

      <div className="grid md:grid-cols-3 gap-3">
        {[
          {
            label: 'QUARTER START',
            title: 'Program Refresh',
            items: ['Clear communication', 'Any changes announced']
          },
          {
            label: 'MID-QUARTER',
            title: 'Check Dashboards',
            items: ['Timely updates', 'Proactive conversations']
          },
          {
            label: 'QUARTER END',
            title: 'Results Shared',
            items: ['Results alignment', 'Payment timing', 'No surprises']
          }
        ].map((phase) => (
          <div key={phase.label} className="p-4 bg-gray-50 rounded-lg text-center">
            <div className="w-3 h-3 rounded-full bg-eventus-purple mx-auto mb-2" />
            <p className="text-xs font-bold text-gray-500 mb-1">{phase.label}</p>
            <h4 className="font-semibold text-gray-700 mb-2">{phase.title}</h4>
            <ul className="text-xs text-gray-600 space-y-1">
              {phase.items.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <Card className="bg-gray-50">
        <CardContent className="p-4">
          <p className="text-gray-700 text-sm">
            Communications will be made to physicians from HR. Your role is to support understanding and offer clarity when questions arise.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function WhyExistsContent(): React.ReactElement {
  return (
    <div className="space-y-4">
      <p className="text-gray-600">
        EQuIP funding is made possible because Eventus achieves its goals. When we succeed as a company, the incentive pool grows.
      </p>

      <Card>
        <CardContent className="p-4">
          <h4 className="font-semibold text-eventus-navy mb-3">Eventus Goals That Fund EQuIP:</h4>
          <ul className="space-y-2">
            {[
              'Grow our business — serve more patients in more places',
              'Improve outcomes for our patients',
              'Ensure consistency in the care we provide',
              'Operate as one engaged Eventus',
              'Build scalable infrastructure for sustained growth'
            ].map((goal, index) => (
              <li key={index} className="flex items-start gap-2">
                <Target className="w-4 h-4 text-eventus-purple mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-700">{goal}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <div className="p-4 bg-eventus-purple/5 border-l-4 border-eventus-purple rounded-r-lg">
        <p className="text-sm text-gray-500 mb-1">When asked "Where does the funding come from?"</p>
        <p className="text-gray-700 italic text-sm">"It comes from Eventus succeeding as a company. When we hit our quality targets, grow sustainably, and deliver consistent care—that's what funds the incentive pool."</p>
      </div>
    </div>
  );
}

function LeaderRolesContent(): React.ReactElement {
  return (
    <div className="space-y-4">
      <p className="text-gray-600">
        State CMOs play a critical role in supporting physician success by reinforcing the purpose of EQuIP, ensuring expectations are clear, and helping physicians understand their performance before quarter-end.
      </p>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Users className="w-5 h-5 text-eventus-purple" />
            State CMO Responsibilities
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {[
              'Reinforce the purpose of EQuIP within an outcomes-centered model',
              'Ensure expectations are clear and consistent',
              'Review performance data during the quarter to support early insight',
              'Help physicians understand trends before quarter-end',
              'Maintain consistent language and expectations across all conversations'
            ].map((item, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                <CheckCircle className="w-4 h-4 text-eventus-purple mt-0.5 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <Card className="bg-eventus-purple/5 border-eventus-purple/20">
        <CardContent className="p-4">
          <p className="text-gray-700 text-sm">
            <strong>The program is not intended to be managed retroactively.</strong> Early visibility and shared understanding are key. If a physician is surprised by their quarter-end results, the communication loop needs to be reevaluated.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function DashboardsContent(): React.ReactElement {
  return (
    <div className="space-y-4">
      <p className="text-gray-600">
        Dashboards aren't for quarter-end judgment—they're for mid-quarter awareness. If a physician is surprised by their results at quarter close, the communication loop needs to be reassessed.
      </p>

      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="w-5 h-5 text-eventus-purple" />
            <h4 className="font-semibold text-eventus-navy">State CMOs should:</h4>
          </div>
          <ul className="space-y-2">
            {[
              { icon: Eye, text: 'Encourage physicians to review dashboards regularly' },
              { icon: MessageSquare, text: 'Use data to guide proactive conversations' },
              { icon: BarChart3, text: 'Focus on trends rather than isolated data points' },
              { icon: Target, text: 'Normalize the concept of performance data as a planning tool' }
            ].map((item, index) => (
              <li key={index} className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-lg bg-eventus-purple/10 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-4 h-4 text-eventus-purple" />
                </div>
                <span className="text-sm text-gray-700">{item.text}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <Card className="bg-gray-50">
        <CardContent className="p-4">
          <h4 className="font-semibold text-eventus-navy mb-2">Frame Data as Insight, Not Judgment</h4>
          <div className="grid md:grid-cols-2 gap-3 text-sm">
            <div className="p-2 bg-red-50 rounded">
              <p className="text-xs text-red-600 font-medium mb-1">Don't say:</p>
              <p className="text-gray-700 italic">"You failed A1C this quarter."</p>
            </div>
            <div className="p-2 bg-green-50 rounded">
              <p className="text-xs text-green-600 font-medium mb-1">Say instead:</p>
              <p className="text-gray-700 italic">"A1C is trending below this quarter's target. Let's look at what might help."</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// Next Steps Section
// ============================================================================

function NextStepsSection(): React.ReactElement {
  return (
    <Section id="next-steps" background="gray">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        Continue Your Preparation
      </h2>

      <div className="grid md:grid-cols-2 gap-4">
        <Link to="/physician/framework">
          <Card className="h-full border-2 border-eventus-purple/20 hover:border-eventus-purple/50 hover:shadow-lg transition-all cursor-pointer group">
            <CardContent className="p-6 flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-xl bg-eventus-purple/10 flex items-center justify-center mb-4 group-hover:bg-eventus-purple transition-colors">
                <Calculator className="w-6 h-6 text-eventus-purple group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-eventus-navy mb-2">
                Framework Reference
              </h3>
              <p className="text-gray-600 text-sm mb-3">
                Detailed reference with interactive calculator and scenarios.
              </p>
              <div className="flex items-center text-eventus-purple font-semibold text-sm">
                View Framework
                <ChevronRight className="w-4 h-4 ml-1" />
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/physician/faq">
          <Card className="h-full border-2 border-eventus-navy/20 hover:border-eventus-navy/50 hover:shadow-lg transition-all cursor-pointer group">
            <CardContent className="p-6 flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-xl bg-eventus-navy/10 flex items-center justify-center mb-4 group-hover:bg-eventus-navy transition-colors">
                <HelpCircle className="w-6 h-6 text-eventus-navy group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-eventus-navy mb-2">FAQ</h3>
              <p className="text-gray-600 text-sm mb-3">
                Frameworks to help you answer the questions your team may ask.
              </p>
              <div className="flex items-center text-eventus-navy font-semibold text-sm">
                View FAQ
                <ChevronRight className="w-4 h-4 ml-1" />
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Success Section */}
      <Card className="mt-8 bg-gradient-to-r from-eventus-purple/10 to-eventus-navy/10">
        <CardContent className="p-8 text-center">
          <h3 className="text-xl font-bold text-eventus-navy mb-4">What Success Looks Like</h3>
          <ul className="text-sm text-gray-700 space-y-2 text-left max-w-md mx-auto mb-6">
            {[
              'Physicians understand how measures connect to Outcomes-Centered Healthcare',
              'No surprises at quarter close',
              'Consistent language and expectations across leaders',
              'Data issues flagged early, not after results are final'
            ].map((item, index) => (
              <li key={index} className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="text-xl font-bold text-eventus-purple">
            Lead with clarity. Answer with confidence.
          </p>
        </CardContent>
      </Card>
    </Section>
  );
}

// ============================================================================
// Main Component
// ============================================================================

interface PhysicianLeaderGuideProps {
  employmentStatus?: EmploymentStatus;
}

export function PhysicianLeaderGuide({ employmentStatus }: PhysicianLeaderGuideProps = {}): React.ReactElement {
  return (
    <div className="min-h-screen bg-gray-50 pb-20 lg:pb-0">
      {/* Floating Navigation */}
      <FloatingNav items={[
        { id: 'framework', label: 'Framework', path: '/physician/framework' },
        { id: 'leader-guide', label: 'Leader Guide', path: '/physician/leader-guide' },
        { id: 'faq', label: 'FAQ', path: '/physician/faq' },
        { id: 'leadership', label: 'Leadership Dashboard', path: '/leadership' },
      ]} />

      {/* Overlay Side Navigation */}
      <OverlaySideNav sections={navSections} />

      {/* Hero Section */}
      <HeroSection />

      {/* TIER 1: Your Role in 60 Seconds */}
      <YourRoleSection />

      {/* TIER 2: The Simple Framework */}
      <SimpleFrameworkSection />

      {/* TIER 3: Detailed Context (Collapsed) */}
      <DetailedContextSection employmentStatus={employmentStatus} />

      {/* Next Steps */}
      <NextStepsSection />
    </div>
  );
}
